import { useSmoothedText } from "@sill/shared/chat/useSmoothedText";
import { motion } from "framer-motion";
import { History } from "lucide-react";

import type { AdvisorMessage } from "../../lib/advisor-types";
import { cn } from "../../lib/utils";
import { AdvisorMarkdown } from "./advisor-markdown";
import { RenderPayloadView } from "./render-registry";

/**
 * StaleSnapshotBadge marks a group of renders that were computed against
 * a profile snapshot the user has since superseded. Visually muted (warning
 * tone, small) — the goal is "subtle disclosure," not alarm. The user can
 * always re-ask to get a fresh widget.
 */
function StaleSnapshotBadge() {
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-warning-foreground"
      data-slot="advisor-stale-snapshot-badge"
      role="status"
    >
      <History aria-hidden className="h-3 w-3" />
      Based on previous inputs
    </div>
  );
}

interface MessageBubbleProps {
  message: AdvisorMessage;
  onFollowup?: (prompt: string) => void;
  showInlineRender?: boolean;
  inlineRenderClassName?: string;
  className?: string;
  currentProfileRecencyToken?: string | null;
  /** True only for the advisor message whose turn is actively streaming. */
  streaming?: boolean;
}

export function AdvisorMessageBubble({
  className,
  currentProfileRecencyToken,
  inlineRenderClassName,
  message,
  onFollowup,
  showInlineRender = false,
  streaming = false,
}: MessageBubbleProps) {
  const isUser = message.role === "user";
  // Token-level smoothing (SILL-459): reveal the advisor text at a steady
  // cadence instead of in raw text_delta bursts. Not-streaming / history
  // messages return the full text instantly; reduced-motion is instant too.
  const smoothedText = useSmoothedText(message.text ?? "", {
    streaming: streaming && !isUser,
  });
  const renders = message.renders ?? (message.render ? [message.render] : []);
  // A widget is "stale" when its source recency token differs from the
  // latest known current-truth token. Empty tokens are treated as fresh:
  // missing a badge is better than falsely claiming a message is stale
  // solely because history writes appended a new snapshot row.
  const isStaleSnapshot =
    Boolean(message.profileRecencyToken) &&
    Boolean(currentProfileRecencyToken) &&
    message.profileRecencyToken !== currentProfileRecencyToken;

  if (isUser) {
    return (
      <div
        className={cn("flex w-full justify-end", className)}
        data-role="user"
      >
        <div className="max-w-[min(32rem,90%)] rounded-3xl rounded-br-lg bg-primary-soft px-4 py-2.5 text-[15px] font-medium leading-[1.45] text-foreground">
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("flex w-full items-start", className)}
      data-role="advisor"
    >
      <div className="min-w-0 flex-1 space-y-2.5">
        {message.text ? (
          <div className="advisor-msg-enter">
            <AdvisorMarkdown suppressTables={renders.length > 0}>
              {smoothedText}
            </AdvisorMarkdown>
          </div>
        ) : null}
        {renders.length > 0 && showInlineRender ? (
          <div
            className={cn("space-y-3 pt-0.5", inlineRenderClassName)}
            data-slot="advisor-inline-render"
            data-stale={isStaleSnapshot ? "true" : undefined}
          >
            {isStaleSnapshot ? <StaleSnapshotBadge /> : null}
            {renders.map((payload, index) => (
              <motion.div
                animate={{ opacity: 1, scale: 1, y: 0 }}
                initial={{ opacity: 0, scale: 0.985, y: 14 }}
                key={`${message.id}-render-${index}`}
                transition={{
                  delay: index * 0.05,
                  duration: 0.28,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <RenderPayloadView payload={payload} />
              </motion.div>
            ))}
          </div>
        ) : null}
        {message.followups && message.followups.length > 0 ? (
          <div
            className="flex flex-wrap gap-1.5 pt-1"
            data-slot="advisor-followup-chips"
          >
            {message.followups.map((prompt) => (
              <button
                className="inline-flex items-center gap-1.5 rounded-full border border-border-soft bg-background px-3 py-1.5 text-[13px] font-medium text-foreground transition hover:border-primary/40 hover:bg-primary-soft/40 hover:text-primary-dark focus-visible:border-primary/50 focus-visible:bg-primary-soft/50 focus-visible:text-primary-dark focus-visible:outline-none"
                data-slot="advisor-followup-chip"
                key={prompt}
                onClick={() => onFollowup?.(prompt)}
                type="button"
              >
                {prompt}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
